import { isBlockEditor } from '../block-boot';

const initialized = new WeakSet();
let currentOpenWrap = null;

const listeningDocuments = new WeakSet();

function listenOnDocument(doc) {
  if (listeningDocuments.has(doc)) return;
  listeningDocuments.add(doc);

  doc.addEventListener('keydown', (e) => {
    if (!currentOpenWrap || e.key !== 'Escape') return;
    const trigger = currentOpenWrap.querySelector('.custom-select__trigger');
    currentOpenWrap.classList.remove('is-open');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
    currentOpenWrap = null;
  });

  doc.addEventListener('click', (e) => {
    if (currentOpenWrap && !currentOpenWrap.contains(e.target)) {
      const trigger = currentOpenWrap.querySelector('.custom-select__trigger');
      currentOpenWrap.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      currentOpenWrap = null;
    }
  });
}

function buildCustomSelect(select) {
  if (initialized.has(select)) return;

  const doc = select.ownerDocument;
  const view = doc.defaultView;
  listenOnDocument(doc);

  const wrap = doc.createElement('div');
  wrap.className = 'custom-select';

  const trigger = doc.createElement('button');
  trigger.type = 'button';
  trigger.className = 'custom-select__trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  const prefix = select.dataset.prefix;
  if (prefix) {
    const prefixEl = doc.createElement('span');
    prefixEl.className = 'custom-select__prefix';
    prefixEl.textContent = prefix;
    trigger.appendChild(prefixEl);
  }

  const labelEl = doc.createElement('span');
  labelEl.className = 'custom-select__label';

  const iconEl = doc.createElement('span');
  iconEl.className = 'custom-select__icon';
  iconEl.setAttribute('aria-hidden', 'true');

  trigger.append(labelEl, iconEl);

  const list = doc.createElement('ul');
  list.className = 'custom-select__options';
  list.setAttribute('role', 'listbox');

  Array.from(select.options).forEach((opt) => {
    const li = doc.createElement('li');
    li.className = 'custom-select__option';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', String(opt.selected));
    li.dataset.value = opt.value;
    li.textContent = opt.text;
    list.appendChild(li);
  });

  select.insertAdjacentElement('beforebegin', wrap);
  wrap.appendChild(select);
  wrap.appendChild(trigger);
  wrap.appendChild(list);

  const optionEls = Array.from(list.querySelectorAll('.custom-select__option'));

  function syncDisplay() {
    const sel = select.options[select.selectedIndex];
    labelEl.textContent = sel ? sel.text : '';
    wrap.classList.toggle('is-placeholder', !select.value);
    optionEls.forEach((li) => {
      li.setAttribute('aria-selected', String(li.dataset.value === select.value));
    });
  }
  syncDisplay();

  function close() {
    wrap.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    if (currentOpenWrap === wrap) currentOpenWrap = null;
  }

  function open() {
    if (currentOpenWrap && currentOpenWrap !== wrap) {
      currentOpenWrap.classList.remove('is-open');
      const otherTrigger = currentOpenWrap.querySelector('.custom-select__trigger');
      if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
    }
    wrap.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    currentOpenWrap = wrap;
  }

  function pick(value) {
    select.value = value;
    select.dispatchEvent(new view.Event('change', { bubbles: true }));
    syncDisplay();
    close();
    trigger.focus();
  }

  trigger.addEventListener('click', () => {
    wrap.classList.contains('is-open') ? close() : open();
  });

  list.addEventListener('click', (e) => {
    const li = e.target.closest('.custom-select__option');
    if (li) pick(li.dataset.value);
  });

  // Forward CF7 validation class to wrapper
  new view.MutationObserver(() => {
    wrap.classList.toggle(
      'custom-select--invalid',
      select.classList.contains('wpcf7-not-valid'),
    );
  }).observe(select, { attributes: true, attributeFilter: ['class'] });

  // Re-sync on native change
  select.addEventListener('change', syncDisplay);
  if (select.form) {
    select.form.addEventListener('reset', () => {
      setTimeout(syncDisplay, 0);
    });
  }

  initialized.add(select);
}

export default function initCustomSelects(root = document) {
  root.querySelectorAll('.wpcf7 select, .js-custom-select').forEach(buildCustomSelect);
}

if (!isBlockEditor()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomSelects());
  } else {
    initCustomSelects();
  }
}
